/**
 * Hook para revelar e-mail OU telefone de um usuário (tenant_user) — LGPD.
 * Chama a RPC `wl_reveal_tenant_user_contact` que registra cada acesso em
 * `security_bypass_attempts` e respeita rate-limit (50/hora por campo).
 *
 * @version 1.0.0
 * RELEASE: 2026-05-29 v1.0.0 - Criação inicial.
 */
import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('WlRevealTenantUserContact');

/** Campos suportados pelo reveal. */
export type WlUserContactField = 'email' | 'phone';

/** Chave do cache local: `${user_id}:${field}`. */
const makeKey = (userId: string, field: WlUserContactField) => `${userId}:${field}`;

export function useWlRevealTenantUserContact() {
  // Cache local da sessão da página (revelações já feitas)
  const [revealed, setRevealed] = useState<Record<string, string | null>>({});

  const mutation = useMutation({
    mutationFn: async (vars: { userId: string; field: WlUserContactField }) => {
      const { data, error } = await (supabase.rpc as any)(
        'wl_reveal_tenant_user_contact',
        { p_user_id: vars.userId, p_field: vars.field },
      );
      if (error) throw error;
      return { ...vars, value: (data as string | null) ?? null };
    },
    onSuccess: ({ userId, field, value }) => {
      setRevealed((prev) => ({ ...prev, [makeKey(userId, field)]: value }));
    },
    onError: (err: unknown) => {
      logger.error(
        '[wl] reveal tenant user contact failed',
        err instanceof Error ? err.message : String(err),
      );
    },
  });

  /** Oculta novamente um valor revelado. */
  const hide = useCallback((userId: string, field: WlUserContactField) => {
    setRevealed((prev) => {
      const next = { ...prev };
      delete next[makeKey(userId, field)];
      return next;
    });
  }, []);

  return {
    /** Valor revelado (ou undefined se ainda não revelado). */
    get: (userId: string, field: WlUserContactField): string | null | undefined =>
      revealed[makeKey(userId, field)],
    reveal: (userId: string, field: WlUserContactField) =>
      mutation.mutate({ userId, field }),
    hide,
    isRevealing: (userId: string, field: WlUserContactField) =>
      mutation.isPending &&
      mutation.variables?.userId === userId &&
      mutation.variables?.field === field,
  };
}
